import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import ApiService from '../../service/ApiService'
import { getDeviceId } from '../../utils/deviceId'
import { matchesVariantCombination } from '../../utils/productPricing'

export const CartContext = createContext()

const CURRENT_VENDOR_KEY = 'currentVendorId'

const normalizeItem = (item) => ({
  cartId: item.cart_id,
  prodId: item.product_id,
  count: item.quantity,
  price: item.price,
  total_price: item.total_price,
  name_en: item.name_en,
  name_ar: item.name_ar,
  image: item.image,
  note: item.note || '',
  variant_combination: item.variant_combination || '',
  is_available: item.is_available,
  available_stock: item.available_stock,
})

const findCartLine = (items, prodId, variantIds = []) => {
  return items.find(
    (item) =>
      item.product_id === prodId &&
      matchesVariantCombination(item.variant_combination, variantIds)
  )
}

export const CartProvider = ({ children }) => {
  const [vendorCarts, setVendorCarts] = useState({})
  const [currentVendorId, setCurrentVendorId] = useState(
    () => parseInt(localStorage.getItem(CURRENT_VENDOR_KEY), 10) || null
  )
  const [loading, setLoading] = useState(false)
  const [vendorSwitchAlert, setVendorSwitchAlert] = useState(null)
  const vendorCartsRef = useRef({})
  const inflightCartFetchesRef = useRef({})

  vendorCartsRef.current = vendorCarts

  const applyCurrentVendor = useCallback((vendorId) => {
    setCurrentVendorId(vendorId)
    localStorage.setItem(CURRENT_VENDOR_KEY, String(vendorId))
  }, [])

  const clearAllCartsLocal = useCallback(() => {
    setVendorCarts({})
    setCurrentVendorId(null)
    localStorage.removeItem(CURRENT_VENDOR_KEY)
  }, [])

  const clearAllCartsRemote = useCallback(async () => {
    await ApiService('/cart/clearCart', { device_id: getDeviceId() }, 'post')
    clearAllCartsLocal()
  }, [clearAllCartsLocal])

  const updateVendorCart = useCallback((vendorId, items, summary = {}) => {
    setVendorCarts(prev => ({
      ...prev,
      [vendorId]: { items, summary }
    }))
  }, [])

  const fetchVendorCart = useCallback(async (vendorId, setAsCurrent = false, options = {}) => {
    const { force = false } = options
    if (!vendorId) return []

    const vendorKey = String(vendorId)

    if (!force && vendorCartsRef.current[vendorId] !== undefined) {
      const items = vendorCartsRef.current[vendorId].items || []
      if (setAsCurrent) applyCurrentVendor(vendorId)
      return items
    }

    if (inflightCartFetchesRef.current[vendorKey]) {
      const items = await inflightCartFetchesRef.current[vendorKey]
      if (setAsCurrent) applyCurrentVendor(vendorId)
      return items
    }

    const request = (async () => {
      const res = await ApiService(`/vendors/${vendorId}/cart`, { device_id: getDeviceId() }, 'get')
      const items = (res?.items || []).map(normalizeItem)
      updateVendorCart(vendorId, items, res?.summary || {})
      return items
    })()

    inflightCartFetchesRef.current[vendorKey] = request

    try {
      const items = await request
      if (setAsCurrent) applyCurrentVendor(vendorId)
      return items
    } finally {
      delete inflightCartFetchesRef.current[vendorKey]
    }
  }, [updateVendorCart, applyCurrentVendor])

  const setCurrentVendor = useCallback(async (vendorId) => {
    if (!vendorId) return
    await fetchVendorCart(vendorId, true)
  }, [fetchVendorCart])

  useEffect(() => {
    if (currentVendorId) {
      fetchVendorCart(currentVendorId, true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const cart = currentVendorId ? (vendorCarts[currentVendorId]?.items || []) : []
  const summary = currentVendorId ? (vendorCarts[currentVendorId]?.summary || {}) : {}

  const getVendorCartItems = useCallback((vendorId) => {
    return vendorCarts[vendorId]?.items || []
  }, [vendorCarts])

  const clearVendorCart = useCallback(async (vendorId) => {
    if (!vendorId) return
    const res = await ApiService(`/vendors/${vendorId}/cart`, { device_id: getDeviceId() }, 'get')
    const items = res?.items || []
    for (const item of items) {
      await ApiService(`/vendors/${vendorId}/cart/remove/${item.cart_id}`, {
        device_id: getDeviceId(),
      }, 'post')
    }
    updateVendorCart(vendorId, [], { total_price: 0, total_items: 0, items_count: 0 })
    if (currentVendorId === vendorId) {
      setCurrentVendorId(null)
      localStorage.removeItem(CURRENT_VENDOR_KEY)
    }
  }, [updateVendorCart, currentVendorId])

  const ensureVendorCartForAdd = useCallback(async (vendorId) => {
    const cachedOtherVendor = Object.entries(vendorCarts).find(
      ([id, data]) => parseInt(id, 10) !== vendorId && data?.items?.length > 0
    )

    if (cachedOtherVendor) {
      const clearedVendorId = parseInt(cachedOtherVendor[0], 10)
      await clearVendorCart(clearedVendorId)
      setVendorSwitchAlert({ clearedVendorId })
      return true
    }

    if (currentVendorId && currentVendorId !== vendorId) {
      const res = await ApiService(`/vendors/${currentVendorId}/cart`, { device_id: getDeviceId() }, 'get')
      if (res?.items?.length > 0) {
        await clearVendorCart(currentVendorId)
        setVendorSwitchAlert({ clearedVendorId: currentVendorId })
        return true
      }
    }

    return false
  }, [currentVendorId, vendorCarts, clearVendorCart])

  const dismissVendorSwitchAlert = useCallback(() => {
    setVendorSwitchAlert(null)
  }, [])

  const addToCart = useCallback(async (prodId, prodPrice, options = {}) => {
    const vendorId = options.vendorId || currentVendorId
    if (!vendorId) return

    await ensureVendorCartForAdd(vendorId)

    const body = {
      product_id: prodId,
      quantity: options.quantity || 1,
      device_id: getDeviceId(),
      vendor_id: vendorId,
    }
    const trimmedNote = options.note?.trim()
    if (trimmedNote) body.note = trimmedNote
    if (options.variant_combination?.length) body.variant_combination = options.variant_combination

    setLoading(true)
    try {
      await ApiService(`/vendors/${vendorId}/cart/add`, body, 'post')
      await fetchVendorCart(vendorId, true, { force: true })
    } finally {
      setLoading(false)
    }
  }, [currentVendorId, fetchVendorCart, ensureVendorCartForAdd])

  const updateItem = useCallback(async (cartId, quantity, vendorId = currentVendorId) => {
    if (!vendorId || !cartId) return
    setLoading(true)
    try {
      await ApiService(`/vendors/${vendorId}/cart/update/${cartId}`, {
        device_id: getDeviceId(),
        quantity,
      }, 'post')
      await fetchVendorCart(vendorId, vendorId === currentVendorId, { force: true })
    } finally {
      setLoading(false)
    }
  }, [currentVendorId, fetchVendorCart])

  const removeItem = useCallback(async (cartId, vendorId = currentVendorId) => {
    if (!vendorId || !cartId) return
    setLoading(true)
    try {
      await ApiService(`/vendors/${vendorId}/cart/remove/${cartId}`, {
        device_id: getDeviceId(),
      }, 'post')
      await fetchVendorCart(vendorId, vendorId === currentVendorId, { force: true })
    } finally {
      setLoading(false)
    }
  }, [currentVendorId, fetchVendorCart])

  const removeProd = useCallback(async (prodId, vendorId = currentVendorId, variantIds = []) => {
    const items = vendorId ? (vendorCarts[vendorId]?.items || []) : cart
    const item = items.find(
      (obj) =>
        obj.prodId === prodId &&
        matchesVariantCombination(obj.variant_combination, variantIds)
    )
    if (item) await removeItem(item.cartId, vendorId)
  }, [cart, vendorCarts, removeItem])

  const decreaseItem = useCallback(async (cartId, vendorId = currentVendorId) => {
    const items = vendorId ? (vendorCarts[vendorId]?.items || []) : cart
    const item = items.find((obj) => obj.cartId === cartId)
    if (!item) return
    if (item.count <= 1) {
      await removeItem(item.cartId, vendorId)
    } else {
      await updateItem(item.cartId, item.count - 1, vendorId)
    }
  }, [cart, vendorCarts, removeItem, updateItem])

  const addMultiProd = useCallback(async (prodId, count, prodPrice, options = {}) => {
    const vendorId = options.vendorId || currentVendorId
    if (!vendorId) return

    await ensureVendorCartForAdd(vendorId)

    const variantIds = options.variant_combination || []
    const res = await ApiService(`/vendors/${vendorId}/cart`, { device_id: getDeviceId() }, 'get')
    const existing = findCartLine(res?.items || [], prodId, variantIds)

    if (existing) {
      await updateItem(existing.cart_id, count, vendorId)
      return
    }

    await addToCart(prodId, prodPrice, { ...options, vendorId, quantity: count })
  }, [currentVendorId, updateItem, addToCart, ensureVendorCartForAdd, fetchVendorCart])

  const initCart = useCallback(async () => {
    setLoading(true)
    try {
      await clearAllCartsRemote()
    } finally {
      setLoading(false)
    }
  }, [clearAllCartsRemote])

  const functions = {
    addToCart,
    removeProd,
    decreaseItem,
    initCart,
    addMultiProd,
    updateItem,
    removeItem,
    fetchVendorCart,
    setCurrentVendor,
    clearVendorCart,
    getVendorCartItems,
    currentVendorId,
    loading,
    summary,
    vendorSwitchAlert,
    dismissVendorSwitchAlert,
  }

  return (
    <CartContext.Provider value={[cart, functions]}>
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const cartManager = useContext(CartContext)
  return cartManager || [[], {}]
}
